import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { cn } from "./utils/cn";

const STIFFNESS = 340;
const MASS = 1;
/** Underdamped — overshoots, then settles. Used once the press is released. */
const RELEASE_DAMPING = 20;
/** Overdamped — leans toward the target and waits while the pointer is down. */
const PRESS_DAMPING = 42;
const PRESS_FRACTION = 0.18;
const PRESS_MIN_PX = 8;
const PRESS_MAX_PX = 22;

type Axis = "x" | "y";
type Mode = "snap" | "spring";

export interface SlidingActiveIndicatorProps {
  /** `data-slide-id` of the committed item (usually the current route). */
  activeId: string | null;
  /** Vertical lists travel on Y; horizontal tab bars travel on X. */
  axis?: Axis;
  /**
   * Change this to jump with no bounce — sidebar collapse/expand, where the
   * item boxes themselves are restyling.
   */
  layoutKey?: string | number | boolean;
  className?: string;
  children?: ReactNode;
}

type Rect = { x: number; y: number; w: number; h: number };
type Chan = { value: number; velocity: number; target: number };

type MotionState = {
  activeId: string | null;
  pendingId: string | null;
  pressing: boolean;
  axis: Axis;
  reduce: boolean;
};

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

function channel(): Chan {
  return { value: 0, velocity: 0, target: 0 };
}

function stepChannel(ch: Chan, dt: number, damping: number) {
  const accel =
    (-STIFFNESS * (ch.value - ch.target) - damping * ch.velocity) / MASS;
  ch.velocity += accel * dt;
  ch.value += ch.velocity * dt;
}

function atRest(ch: Chan) {
  return Math.abs(ch.value - ch.target) < 0.4 && Math.abs(ch.velocity) < 6;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function findItem(container: HTMLElement, id: string | null) {
  if (!id) return null;
  const nodes = container.querySelectorAll<HTMLElement>("[data-slide-id]");
  for (const node of nodes) {
    if (node.dataset.slideId === id) return node;
  }
  return null;
}

function measureItem(item: HTMLElement, container: HTMLElement): Rect {
  const c = container.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  return {
    x: r.left - c.left,
    y: r.top - c.top,
    w: r.width,
    h: r.height,
  };
}

function lean(from: number, to: number) {
  const delta = to - from;
  if (delta === 0) return from;
  const distance = Math.abs(delta);
  const travel = Math.min(
    PRESS_MAX_PX,
    Math.max(PRESS_MIN_PX, distance * PRESS_FRACTION)
  );
  return from + Math.sign(delta) * Math.min(travel, distance);
}

/**
 * One highlight that travels between sibling items.
 *
 * Render it inside a `relative` container. Each destination needs
 * `data-slide-id`. Pointer-down leans the highlight toward that item and
 * holds; pointer-up springs the rest of the way with a damped bounce.
 * Leaving the item, or releasing outside it, springs back. Items with
 * `data-slide-disabled="true"` ignore the press.
 *
 * Updating `activeId` (route changes, browser back) springs the same way.
 * `layoutKey` changes and resizes snap, so collapse/expand does not bounce.
 */
export default function SlidingActiveIndicator({
  activeId,
  axis = "y",
  layoutKey,
  className,
  children,
}: SlidingActiveIndicatorProps) {
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const stateRef = useRef<MotionState>({
    activeId,
    pendingId: null,
    pressing: false,
    axis,
    reduce: false,
  });
  const syncRef = useRef<(mode: Mode) => void>(() => {});
  const skipActive = useRef(true);
  const skipLayout = useRef(true);

  stateRef.current.activeId = activeId;
  stateRef.current.axis = axis;

  useIsoLayoutEffect(() => {
    const el = indicatorRef.current;
    const container = el?.parentElement;
    if (!el || !container) return;

    let alive = true;
    let frame = 0;
    let running = false;
    let last = 0;
    let damping = RELEASE_DAMPING;
    const x = channel();
    const y = channel();
    const w = channel();
    const h = channel();

    const apply = (scaleX: number, scaleY: number) => {
      el.style.width = `${Math.max(0, w.value)}px`;
      el.style.height = `${Math.max(0, h.value)}px`;
      el.style.transform = `translate3d(${x.value}px, ${y.value}px, 0) scale(${scaleX}, ${scaleY})`;
    };

    const hide = () => {
      el.style.opacity = "0";
    };

    const show = () => {
      el.style.opacity = "1";
    };

    const readTarget = (): Rect | null => {
      const state = stateRef.current;
      const active = findItem(container, state.activeId);
      if (!active) return null;
      const activeRect = measureItem(active, container);
      if (activeRect.w < 1 || activeRect.h < 1) return null;

      const pending = findItem(container, state.pendingId);
      if (!pending || state.pendingId === state.activeId) return activeRect;
      const pendingRect = measureItem(pending, container);
      if (pendingRect.w < 1 || pendingRect.h < 1) return activeRect;

      if (state.pressing) {
        return state.axis === "y"
          ? { ...activeRect, y: lean(activeRect.y, pendingRect.y) }
          : { ...activeRect, x: lean(activeRect.x, pendingRect.x) };
      }
      return pendingRect;
    };

    const stop = () => {
      running = false;
      cancelAnimationFrame(frame);
    };

    const kick = () => {
      if (running) return;
      running = true;
      last = 0;
      frame = requestAnimationFrame(loop);
    };

    const loop = (now: number) => {
      if (!alive) return;
      const dt = Math.min(0.032, last === 0 ? 1 / 60 : (now - last) / 1000);
      last = now;
      let remaining = dt;
      while (remaining > 0) {
        const step = Math.min(remaining, 1 / 120);
        stepChannel(x, step, damping);
        stepChannel(y, step, damping);
        stepChannel(w, step, damping);
        stepChannel(h, step, damping);
        remaining -= step;
      }

      const travelV = stateRef.current.axis === "y" ? y.velocity : x.velocity;
      const stretch = Math.min(0.14, Math.abs(travelV) * 0.00035);
      const along = 1 + stretch;
      const across = 1 / along;
      const scaleX = stateRef.current.axis === "x" ? along : across;
      const scaleY = stateRef.current.axis === "y" ? along : across;

      if ([x, y, w, h].every(atRest)) {
        x.value = x.target;
        y.value = y.target;
        w.value = w.target;
        h.value = h.target;
        x.velocity = 0;
        y.velocity = 0;
        w.velocity = 0;
        h.velocity = 0;
        apply(1, 1);
        stop();
        return;
      }

      apply(scaleX, scaleY);
      frame = requestAnimationFrame(loop);
    };

    const sync = (mode: Mode) => {
      if (!alive) return;
      const rect = readTarget();
      if (!rect) {
        hide();
        stop();
        return;
      }
      show();
      x.target = rect.x;
      y.target = rect.y;
      w.target = rect.w;
      h.target = rect.h;
      damping = stateRef.current.pressing ? PRESS_DAMPING : RELEASE_DAMPING;

      if (mode === "snap" || stateRef.current.reduce) {
        x.value = x.target;
        y.value = y.target;
        w.value = w.target;
        h.value = h.target;
        x.velocity = 0;
        y.velocity = 0;
        w.velocity = 0;
        h.velocity = 0;
        apply(1, 1);
        stop();
        return;
      }
      kick();
    };

    syncRef.current = sync;

    const watchItems = () => {
      container
        .querySelectorAll<HTMLElement>("[data-slide-id]")
        .forEach((node) => ro.observe(node));
    };

    const ro = new ResizeObserver(() => {
      watchItems();
      sync("snap");
    });
    ro.observe(container);
    watchItems();

    let pressedId: string | null = null;

    const itemFrom = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null;
      const item = target.closest<HTMLElement>("[data-slide-id]");
      if (!item || !container.contains(item)) return null;
      return item;
    };

    const cancelPress = () => {
      if (!pressedId && !stateRef.current.pressing) return;
      pressedId = null;
      stateRef.current.pressing = false;
      stateRef.current.pendingId = null;
      sync("spring");
    };

    const onDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const item = itemFrom(event.target);
      if (!item || item.dataset.slideDisabled === "true") return;
      pressedId = item.dataset.slideId ?? null;
      stateRef.current.pendingId = pressedId;
      stateRef.current.pressing = true;
      sync("spring");
    };

    const onUp = (event: PointerEvent) => {
      if (!pressedId) return;
      const item = itemFrom(event.target);
      const commit =
        !!item &&
        item.dataset.slideId === pressedId &&
        item.dataset.slideDisabled !== "true";
      const released = pressedId;
      pressedId = null;
      stateRef.current.pressing = false;
      stateRef.current.pendingId =
        commit && released !== stateRef.current.activeId ? released : null;
      sync("spring");
    };

    const onMove = (event: PointerEvent) => {
      if (!pressedId) return;
      const item = itemFrom(event.target);
      if (item?.dataset.slideId !== pressedId) cancelPress();
    };

    container.addEventListener("pointerdown", onDown);
    container.addEventListener("pointermove", onMove);
    container.addEventListener("pointerleave", cancelPress);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", cancelPress);

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => {
      stateRef.current.reduce = prefersReducedMotion();
      sync(stateRef.current.reduce ? "snap" : "spring");
    };
    stateRef.current.reduce = prefersReducedMotion();
    motion.addEventListener("change", onMotion);

    sync("snap");

    return () => {
      alive = false;
      stop();
      ro.disconnect();
      container.removeEventListener("pointerdown", onDown);
      container.removeEventListener("pointermove", onMove);
      container.removeEventListener("pointerleave", cancelPress);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", cancelPress);
      motion.removeEventListener("change", onMotion);
      syncRef.current = () => {};
    };
  }, []);

  useIsoLayoutEffect(() => {
    if (!stateRef.current.pressing && stateRef.current.pendingId === activeId) {
      stateRef.current.pendingId = null;
    }
    if (skipActive.current) {
      skipActive.current = false;
      return;
    }
    syncRef.current("spring");
  }, [activeId, axis]);

  useIsoLayoutEffect(() => {
    if (skipLayout.current) {
      skipLayout.current = false;
      return;
    }
    syncRef.current("snap");
  }, [layoutKey]);

  return (
    <span
      ref={indicatorRef}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute left-0 top-0 z-0 opacity-0",
        className
      )}
    >
      {children}
    </span>
  );
}
